import React, { useEffect, useReducer, useState } from 'react';
import { EnvironmentVariables, LtsInfoData } from '../types';
import CurrentStationDetail from './components/CurrentStationDetail';
import LiveTrainBetweenTwoStations from './components/LiveTrainBetweenTwoStations';
import CheckPnrStatus from './components/CheckPnrStatus';
import LtsDatePicker from './components/LtsDatePicker';
import LtsContentView from './components/LtsContentView';
import WhyBook from '../components/RecommendTripPathList/WhyBookWithUs';
import LtsTrainDetailsTable from './components/LtsTrainDetailsTable';
import { searchWidgetInitialState, searchWidgetReducer } from '../../reducers/searchWidgetReducer';
import SearchWidget from '../components/SearchWidget/SearchWidget';
import { TrainClasses } from '../route_info/TrainClasses';
import HtmlHeader from '../components/HtmlHeader';
import { BreadCrumb, FaqSection, FooterSection } from '../components';
import QAPage from '../components/QAPage';
import { liveTrainStatusReducer, ltsPageInitialState } from '../../reducers/liveTrainStatusReducer';
import '../../styles/LtsSeo.css';
import '../../styles/base.css';
import '../../styles/railSeoSprite.css';
import TrainNameNumber from './components/TrainNameNumber';
import { decorateLtsData, getLtsStationDetails } from './utils/LtsUtils';
import { getAppEnv } from '../../../config/env';
import RecommendTripPathWrapper from '../components/RecommendTripPathList/RecommendTripPathList';
import { RECOMMEND_PATH } from '../components/RecommendTripPathList/utils/recommendTripPathListUtils';
import LtsTrainRelatedInfoSection from './components/LtsTrainRelatedInfoSection';
import LtsBottomSheet from './components/LtsBottomSheet';
import MealOrderingBottomBanner from './components/MealOrderingBottomBanner';
import { REFRESH_CLICKED, TRAIN_LTS_PAGENAME } from './constants/LtsTrackingConstants';
import useRefreshTimer from './hooks/useRefreshTimer';
import { ACTION_UPDATE_LTS_STATION_DETAILS } from '../../actions/liveTrainStatusActions';
import { formatDateForLtsApiRequest, getDateObjectFromDetails } from './utils/commonLtsUtils';
import { trackClickEventLTS } from '../../utils/omnitureUtils';
import { PROP_63 } from '../../utils/omnitureUtilConstants';
import { logLtsPageLoadEvent } from '../../analytics/events/logLtsPageLoadEvent';

const env = getAppEnv();
const { DATA_LOADER_URL } = env.raw as EnvironmentVariables;
const SearchWidgetContext = React.createContext<any>(searchWidgetInitialState);
export const LiveTrainStatusContext = React.createContext<any>(ltsPageInitialState);

type LtsPageProps = {
  data: LtsInfoData;
};

export function LiveTrainStatus({ data }: LtsPageProps) {
  const { template, ltsResponse } = data;
  const [searchWidgetState, searchWidgetDispatch] = useReducer(searchWidgetReducer, searchWidgetInitialState);
  const [ltsPageState, ltsPageDispatch] = useReducer(liveTrainStatusReducer, ltsResponse);
  const [toggleBottomSheet, setToggleBottomSheet] = useState(false);
  const [showMealBanner, setShowMealBanner] = useState(true);

  const {
    template: { h1_title: ltsPageHeadTitle }
  } = data;

  const ToggleBottomSheetHandler = () => {
    setToggleBottomSheet(!toggleBottomSheet);
  };

  const { trainStartDate = '', trainNumber = '' }: any = ltsPageState;

  const refreshTimerAndLtsData = async () => {
    const ltsResponseClientSide = await getLtsStationDetails({
      trainNumber,
      trainStartDate: formatDateForLtsApiRequest(
        getDateObjectFromDetails({ dateInString: trainStartDate, timeInString: '00:00:00' })
      )
    });
    ltsPageDispatch({
      type: ACTION_UPDATE_LTS_STATION_DETAILS,
      data: ltsResponseClientSide
    });
  };

  const { callbackForManualRefresh, updateTimeText, updateAgoDescription } = useRefreshTimer({
    timerInitialValue: 0,
    refreshIntervalInMilliseconds: 60000,
    updateTimeThresholdInMinutes: 2,
    callbackOnTimeOut: refreshTimerAndLtsData
  });

  const clickRefreshHandler = (event) => {
    event.stopPropagation();
    trackClickEventLTS(REFRESH_CLICKED, PROP_63);
    callbackForManualRefresh();
  };

  useEffect(() => {
    if (typeof window !== 'undefined' && data?.liveStatusApiResponse?.TrainDetails?.Number) {
      logLtsPageLoadEvent(data.liveStatusApiResponse);
    }
  }, []);

  return (
    <LiveTrainStatusContext.Provider value={{ ltsPageState, ltsPageDispatch }}>
      <HtmlHeader data={data} />
      <SearchWidgetContext.Provider value={{ searchWidgetState, searchWidgetDispatch }}>
        <SearchWidget
          openDatePickerOnMount={false}
          topFix={true}
          classNode={TrainClasses.FC}
          pageWiseContext={SearchWidgetContext}
          pageName={TRAIN_LTS_PAGENAME}
          modifySearchProps={true}
        />
      </SearchWidgetContext.Provider>
      <main className="container ltsSeoPage">
        <BreadCrumb breadcrumb={data.template.breadcrumb} customClass={'railSeoWrap__brdCrumb appendBottom30'} />
        <h1 className="ltsSeoPage__title">{ltsPageHeadTitle}</h1>
        {/*<OfflineLtsCallout />*/}
        <div className="mobileShow">
          <TrainNameNumber />
        </div>
        <div className="makeFlex ltsSeo__left">
          <div className="ltsSeo__stickyContainer desktopShow">
            <CurrentStationDetail
              customClass="appendBottom20"
              ltsPageState={ltsPageState}
              clickRefreshHandler={clickRefreshHandler}
              updateTimeText={updateTimeText}
              updateAgoDescription={updateAgoDescription}
            />
            <LiveTrainBetweenTwoStations customClass="appendBottom15" />
            <CheckPnrStatus />
          </div>
          <div className="ltsSeo__right">
            <LtsDatePicker customClass="appendBottom20" />
            <LtsContentView customClass="appendBottom40" />
          </div>
        </div>
        <div className="mobileShow">
          <CurrentStationDetail
            onToggleBottomSheet={ToggleBottomSheetHandler}
            ltsPageState={ltsPageState}
            clickRefreshHandler={clickRefreshHandler}
            updateTimeText={updateTimeText}
            updateAgoDescription={updateAgoDescription}
          />
          {toggleBottomSheet && <LtsBottomSheet onToggleBottomSheet={ToggleBottomSheetHandler} />}
        </div>
        <div className="ltsSeo_additionalInfo">
          <WhyBook customClass="appendBottom40" usps={data.usps} />
          <LtsTrainRelatedInfoSection trainRelatedInfo={data.trainRelatedInfo} />
          {/*
          TODO: Ad components and Banners to be obtained from Ads team, HTML team
          <AdComp customClass="appendBottom40" />
          */}
          <LtsTrainDetailsTable customClass="appendBottom40" stationUrlsInterlinking={data.stationUrlsInterlinking} />
          <CheckPnrStatus customClass="appendBottom40 mobileShow" />
        </div>
        <section style={{ flexWrap: 'wrap' }} className="railSeoWrap__stnDetails makeFlex">
          <RecommendTripPathWrapper recommendPaths={RECOMMEND_PATH.LTS_INFO_PAGE(data)} />
        </section>
        <FaqSection
          faq={data.faq.list}
          title={data.faq.title}
          type="covidSpecialTrains"
          pageName={TRAIN_LTS_PAGENAME}
        />
        {data.geoFaqList?.list && data.geoFaqList.list.length > 0 && (
          <QAPage faq={data.geoFaqList.list} title={data.geoFaqList?.title} pageName={TRAIN_LTS_PAGENAME} />
        )}
      </main>
      <FooterSection template={template} />
      {showMealBanner && ltsPageState?.haltingStationCollection?.some((station) => station.ZomatoMealsAvailable) && (
        <MealOrderingBottomBanner
          onClose={() => setShowMealBanner(false)}
          isTrainRunning={!!(ltsPageState?.currentStation && ltsPageState?.trainStartedFromSource)}
        />
      )}
    </LiveTrainStatusContext.Provider>
  );
}

LiveTrainStatus.fetchData = async (_, match, urlObj) => {
  const {
    params: { trainNumber, templateId }
  } = match;
  const url = `${DATA_LOADER_URL}/api/ltsTemplateData/${templateId}/${trainNumber}`;
  let responseData;

  try {
    const response = await fetch(url);
    const textResponse = await response.text();
    responseData = JSON.parse(textResponse);
    const { liveStatusApiResponse, scheduledIntermediateApiResponse, trainStartDate, ...remaining } = responseData;
    const ltsResponse = decorateLtsData({
      liveStatusApiResponse,
      scheduledIntermediateApiResponse,
      trainStartDate
    });
    return {
      ltsResponse,
      liveStatusApiResponse,
      ...remaining
    };
  } catch (error) {
    /* eslint-disable no-console */
    console.log('DataLoader Api Error ', error);
  }
};

export default LiveTrainStatus;
